import React, { useEffect, useState } from "react";
import CustomCard from "../../../../../components/CustomCard";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TOTAL_MEALS_COUNT_LIST_MODEL } from "../Models/Model";
import CustomTooltip from "../../../../../components/CustomTooltip";
import { currencyFormat } from "../../../../../Apis/functions";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @component TotalMealsCountListView
 * @description
 * Renders a responsive list of KPI cards for total meals.
 * - Fetches KPI data from Redux state (`FoodieOrdersSlice.getTotalMealsKpisList`).
 * - Maps data dynamically to cards defined in `TOTAL_MEALS_COUNT_LIST_MODEL`.
 * - Displays icon, title, optional currency symbol, and value.
 * - Uses `CustomTooltip` for proper text overflow handling.
 *
 * @returns {JSX.Element} List of total meals KPI cards.
 */
const TotalMealsCountListView = () => {
    const { t } = useTranslation();

    // Get KPI list from Redux
    const { getTotalMealsKpisList } = useSelector((state) => state?.FoodieOrdersSlice);

    // Local state to hold list items, initialized with model
    const [items, setItems] = useState({ ...TOTAL_MEALS_COUNT_LIST_MODEL });

    useEffect(() => {
        if (getTotalMealsKpisList !== null && Object.keys(getTotalMealsKpisList)?.length > 0) {

            let aux = { ...items };

            // Map Redux data into local items, adding currency formatting if required
            Object.entries(getTotalMealsKpisList)?.map(([key, value]) => {
                aux = {
                    ...aux,
                    [key]: {
                        ...aux[key],
                        value: aux[key]?.moneySymbol !== "" ? "$ " + currencyFormat(value) : value
                    }
                };
            });

            // Update local state with mapped items
            setItems(aux);
        }
    }, [getTotalMealsKpisList]);

    return (
        <div className={"row d-flex mt-3"}>
            {
                Object.entries(items).map(([key, item]) => {
                    return (
                        <div key={key} className={`col-sm-12 col-md-3 col-lg-3 col-xl-3 col-xxl-3 mb-2 ${item.paddingCard}`}>
                            <CustomCard classCard={`${item.classCard} card-border-8px`} classContent={"px-8px py-16px"} classes="h-100">
                                <div className={"row"}>
                                    <div className={"d-flex justify-content-start align-items-center p-0"}>
                                        {/* Icon representing the KPI */}
                                        <div>
                                            <item.Icon className={item.iconColor} />
                                        </div>

                                        <div className={"flex-grow-1 minw-0 ml-2"}>
                                            {/* Title of the KPI with optional currency symbol */}
                                            <p className={`${item.fontColor} ibl-font-w-700 fs-14px`}>
                                                {t(item.titleTrans)} {item.moneySymbol}
                                            </p>

                                            {/* Value with tooltip for overflow */}
                                            <CustomTooltip
                                                text={item.value}
                                                classes={`${item.fontColor} ibl-font-w-700 fs-24px text-ellipsis-1-row`}
                                                showIfTruncated={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CustomCard>
                        </div>
                    );
                })
            }
        </div>
    );
};

export default TotalMealsCountListView;
